import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  People,
  Inventory,
  AttachMoney,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { format } from 'date-fns';
import { RootState, DashboardStats, Order } from '../../types';
import { AppDispatch } from '../../store/store';
import { fetchDashboardStats, fetchDashboardStatsFiltered } from '../../store/slices/adminSlice';
import DashboardDatePicker from '../../components/admin/DashboardDatePicker';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice } from '../../utils/currency';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
}

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  label?: string;
}

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboardStats: stats, loading } = useSelector((state: RootState) => state.admin);
  const { currencySettings } = useSettings();
  
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    label: 'All Time',
  });

  useEffect(() => {
    // Load initial stats
    dispatch(fetchDashboardStats());
  }, [dispatch]);


  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
    
    // Fetch filtered data
    if (newDateRange.startDate || newDateRange.endDate) {
      const startDate = newDateRange.startDate ? format(newDateRange.startDate, 'yyyy-MM-dd') : undefined;
      const endDate = newDateRange.endDate ? format(newDateRange.endDate, 'yyyy-MM-dd') : undefined;
      
      dispatch(fetchDashboardStatsFiltered({ startDate, endDate }));
    } else {
      // If no dates selected, fetch all stats
      dispatch(fetchDashboardStats());
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const chartData = {
    labels: Array.isArray(stats?.monthlyRevenue) 
      ? stats.monthlyRevenue.map((d, index) => format(new Date(new Date().getFullYear(), index, 1), 'MMM'))
      : [],
    datasets: [
      {
        label: 'Revenue',
        data: Array.isArray(stats?.monthlyRevenue) 
          ? stats.monthlyRevenue.map(d => Number(d.revenue) || 0) 
          : [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Monthly Revenue',
      },
    },
  };

  const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Dashboard
        </Typography>
        <DashboardDatePicker
          value={dateRange}
          onChange={setDateRange}
          onApply={handleDateRangeChange}
        />
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatPrice(Number(stats?.totalRevenue || 0), currencySettings)}
            icon={<AttachMoney sx={{ color: 'white' }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Orders"
            value={stats?.totalOrders || 0}
            icon={<ShoppingCart sx={{ color: 'white' }} />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={<People sx={{ color: 'white' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={stats?.totalProducts || 0}
            icon={<Inventory sx={{ color: 'white' }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Line data={chartData} options={chartOptions} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>Pending Orders</Typography>
                <Chip label={stats?.ordersByStatus?.['PENDING'] || 0} color="warning" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>Processing Orders</Typography>
                <Chip label={stats?.ordersByStatus?.['PROCESSING'] || 0} color="info" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>Shipped Orders</Typography>
                <Chip label={stats?.ordersByStatus?.['SHIPPED'] || 0} color="primary" size="small" />
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>Delivered Orders</Typography>
                <Chip label={stats?.ordersByStatus?.['DELIVERED'] || 0} color="success" size="small" />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 4 }}>
        {/* Low Stock Alerts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Low Stock Alerts
            </Typography>
            <Box sx={{ mt: 2 }}>
              {stats?.lowStockProducts && stats.lowStockProducts.length > 0 ? (
                stats.lowStockProducts.map((product, index) => (
                  <Box key={product.id} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {product.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mr: 2 }}>
                      {product.currentStock} left
                    </Typography>
                    <Chip
                      size="small"
                      label={product.status}
                      color={
                        product.status === 'CRITICAL'
                          ? 'error'
                          : product.status === 'LOW'
                          ? 'warning'
                          : 'secondary'
                      }
                    />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">All products are well-stocked</Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Conversion Rates */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Conversion Rates
            </Typography>
            <Box sx={{ mt: 2 }}>
              {stats?.conversionRates ? (
                <>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Total Customers</Typography>
                    <Typography fontWeight="bold">{stats.conversionRates.totalCustomers}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Customers with Orders</Typography>
                    <Typography fontWeight="bold">{stats.conversionRates.customersWithOrders}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Repeat Customers</Typography>
                    <Typography fontWeight="bold">{stats.conversionRates.repeatCustomers}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Customer → Order Rate</Typography>
                    <Chip 
                      label={`${(stats.conversionRates.customerToOrderRate * 100).toFixed(1)}%`} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Order Completion Rate</Typography>
                    <Chip 
                      label={`${(stats.conversionRates.orderCompletionRate * 100).toFixed(1)}%`} 
                      color="success" 
                      size="small" 
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Repeat Customer Rate</Typography>
                    <Chip 
                      label={`${(stats.conversionRates.repeatCustomerRate * 100).toFixed(1)}%`} 
                      color="info" 
                      size="small" 
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Cancellation Rate</Typography>
                    <Chip 
                      label={`${(stats.conversionRates.orderCancellationRate * 100).toFixed(1)}%`} 
                      color="error" 
                      size="small" 
                    />
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary">No conversion data available</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Orders
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats?.recentOrders?.map((order: Order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.orderNumber}</TableCell>
                    <TableCell>{order.user?.email}</TableCell>
                    <TableCell>{formatPrice(order.totalAmount, currencySettings)}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        color={
                          order.status === 'DELIVERED'
                            ? 'success'
                            : order.status === 'PENDING'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {order.createdAt ? format(new Date(order.createdAt), 'MMM dd, yyyy') : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;